// Standard justification texts for the anskaffelsesloven § 5b/§ 5c climate/environment rule. They
// cover the § 5b specification route (requirements in the kravspesifikasjon give a clearly better
// effect) and the three § 5c exemptions (negligible impact; essential health/safety/preparedness
// interests; mandatory standardised minimum requirements). Each is a starting template the buyer must
// adapt — KOFA practice (2025/322, 2025/356) requires a real, procurement-specific assessment, not a
// copied boilerplate. Keys are translated in locales/{en,no}/translation.json.

import { AwardCriterionType } from "../../../../../autogen/bff-api";

// t('Negligible environmental impact')
// t('The environmental impact of this procurement is negligible because [describe why — e.g. small volume, standardized goods with no meaningful environmental differentiation between suppliers]. Climate and environmental considerations therefore carry little weight in distinguishing the tenders.')
// t('Environmental requirements in the specification')
// t('Climate and environmental considerations are addressed as requirements in the specification rather than as an award criterion, because [describe why this gives a clearly better effect — e.g. a binding minimum requirement guarantees the environmental outcome more effectively than a weighted criterion]. The requirements are: [list the relevant environmental requirements].')
// t('Essential interests of health, safety or emergency preparedness')
// t('Climate and environmental requirements are not used because they would compromise essential interests of health, safety or emergency preparedness. Specifically, [describe the essential interest and why environmental weighting would compromise it].')
// t('Mandatory standardised minimum requirements')
// t('Statutory standardised minimum requirements for climate footprint or environmental impact already apply to this procurement, specifically [reference the requirement in law or regulation]. A separate weighting or award criterion is therefore not used.')

export interface EnvironmentJustificationTemplate {
  key: string;
  titleKey: string;
  bodyKey: string;
}

export const ENVIRONMENT_JUSTIFICATION_TEMPLATES: readonly EnvironmentJustificationTemplate[] = [
  {
    key: "addressed-in-specification",
    titleKey: "Environmental requirements in the specification",
    bodyKey:
      "Climate and environmental considerations are addressed as requirements in the specification rather than as an award criterion, because [describe why this gives a clearly better effect — e.g. a binding minimum requirement guarantees the environmental outcome more effectively than a weighted criterion]. The requirements are: [list the relevant environmental requirements].",
  },
  {
    key: "exempt-insignificant",
    titleKey: "Negligible environmental impact",
    bodyKey:
      "The environmental impact of this procurement is negligible because [describe why — e.g. small volume, standardized goods with no meaningful environmental differentiation between suppliers]. Climate and environmental considerations therefore carry little weight in distinguishing the tenders.",
  },
  {
    key: "exempt-critical-interest",
    titleKey: "Essential interests of health, safety or emergency preparedness",
    bodyKey:
      "Climate and environmental requirements are not used because they would compromise essential interests of health, safety or emergency preparedness. Specifically, [describe the essential interest and why environmental weighting would compromise it].",
  },
  {
    key: "exempt-national-minimum",
    titleKey: "Mandatory standardised minimum requirements",
    bodyKey:
      "Statutory standardised minimum requirements for climate footprint or environmental impact already apply to this procurement, specifically [reference the requirement in law or regulation]. A separate weighting or award criterion is therefore not used.",
  },
];

export type EnvironmentJustificationTemplateKey =
  (typeof ENVIRONMENT_JUSTIFICATION_TEMPLATES)[number]["key"];

// Maps each § 5c exemption criterion type to its justification template key.
export const exemptionTemplateKey = (type: AwardCriterionType): EnvironmentJustificationTemplateKey => {
  switch (type) {
    case AwardCriterionType.QualityEnvironmentalExemptCriticalInterest:
      return "exempt-critical-interest";
    case AwardCriterionType.QualityEnvironmentalExemptNationalMinimum:
      return "exempt-national-minimum";
    default:
      return "exempt-insignificant";
  }
};

// The i18n key labelling an exemption type (its template title), for the reason menu/selector.
export const exemptionTitleKey = (type: AwardCriterionType): string =>
  ENVIRONMENT_JUSTIFICATION_TEMPLATES.find((tpl) => tpl.key === exemptionTemplateKey(type))?.titleKey ?? type;

// The translated body text for a template, used to pre-fill the justification box. `translate` is
// the i18next `t` (untyped to avoid pulling the TFunction generic into a util module).
export const templateBody = (
  key: EnvironmentJustificationTemplateKey,
  translate: (k: string) => string,
): string => {
  const template = ENVIRONMENT_JUSTIFICATION_TEMPLATES.find((tpl) => tpl.key === key);
  return template ? translate(template.bodyKey) : "";
};

// True when the justification still equals the unedited template body — drives the non-blocking
// "you forgot to adapt the standard text" warning. KOFA practice requires a procurement-specific
// assessment, not copied boilerplate. Both sides are trimmed so trailing whitespace doesn't matter.
export const isUnchangedFromTemplate = (text: string, body: string): boolean =>
  text.trim().length > 0 && text.trim() === body.trim();

// True when the justification still equals one of the unedited standard template bodies (in the
// current language). The webapp sends this to the BFF (environmentWeightingJustificationIsStandardText)
// so the publish gate can reject a copied boilerplate without the BFF having to match text — keeping
// the check robust to template/format changes.
export const isStandardText = (text: string, translate: (k: string) => string): boolean =>
  ENVIRONMENT_JUSTIFICATION_TEMPLATES.some((tpl) => isUnchangedFromTemplate(text, translate(tpl.bodyKey)));
